import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'es';
interface TranslationContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}
const translations = {
  en: {
    // Navbar
    'nav.aboutUs': 'About Us',
    'nav.funding': 'Funding',
    'nav.howItWorks': 'How it Works',
    'nav.requirements': 'Who Qualifies',
    'nav.testimonials': 'Testimonials',
    'nav.faq': 'FAQ',
    'nav.applyNow': 'SCHEDULE CONSULTATION',
    // Hero
    'hero.badge': 'Commercial Funding',
    'hero.title': 'Commercial Funding for Business Owners in the United States',
    'hero.titleHighlight': '',
    'hero.titleSuffix': '',
    'hero.subtitle': 'We offer financing options for all types of businesses. We evaluate the best alternatives available for you with a simple and personalized process.',
    'hero.cta': 'APPLY NOW',
    'hero.ctaSecondary': 'SCHEDULE CONSULTATION',
    'hero.microcopy1': 'No personal credit impact',
    'hero.microcopy2': 'No initiation fee',
    'hero.microcopy3': 'Approvals in 48 hrs',
    'hero.trustedBy': 'Trusted by 5,000+ Owners',
    'hero.trustpilot': 'Excellent on Trustpilot',
    'hero.approvalBadge1': 'Funding Approved',
    'hero.approvalBadge2': 'Approved',
    'hero.approvalBadge3': 'Approved',
    // Benefits Ticker
    'ticker.fastApprovals': 'Fast Approvals',
    'ticker.noUpfront': 'No Upfront Fees',
    'ticker.softCredit': 'Soft Credit Pull',
    'ticker.flexible': 'Flexible Terms',
    'ticker.sameDay': 'Same-Day Funding',
    'ticker.approval': 'Multiple Options',
    // Value Proposition
    'value.title': 'The Simplest Way to Get Capital',
    'value.titleHighlight': 'for Your Business',
    'value.subtitle': 'At Brixen Funding, we simplify business financing by connecting you with the right lender for your profile.',
    'value.feature1.title': 'Verified Lenders',
    'value.feature1.desc': 'Access to 100+ verified funding partners.',
    'value.feature2.title': 'Tailored Financing',
    'value.feature2.desc': 'Solutions adapted to your business real needs.',
    'value.feature3.title': 'Fast Approvals',
    'value.feature3.desc': 'Responses in as little as 24 hours.',
    'value.feature4.title': 'No Personal Guarantee',
    'value.feature4.desc': '(in most cases)',
    // Funding Solutions
    'solutions.title': 'Funding Solutions',
    'solutions.titleHighlight': 'for Every Business',
    'solutions.subtitle': 'We connect you with the best option based on your industry, revenue, and goals.',
    'solutions.card1.title': 'Business Lines of Credit',
    'solutions.card1.desc': 'Revolving capital. Pay only what you use.',
    'solutions.card2.title': '0% Interest Business Credit',
    'solutions.card2.desc': 'Up to $150,000 with 0% APR for 12 months (subject to qualification).',
    'solutions.card3.title': 'Working Capital',
    'solutions.card3.desc': 'Quick liquidity for payroll, inventory, or expansion.',
    'solutions.card4.title': 'Revenue Based Funding',
    'solutions.card4.desc': 'Flexible payments based on sales. Low credit accepted.',
    'solutions.card5.title': 'Equipment Financing',
    'solutions.card5.desc': 'Acquire machinery, vehicles, or technology without depleting capital.',
    'solutions.card6.title': 'Term Loans',
    'solutions.card6.desc': 'Traditional options with fixed payments and extended terms.',
    'solutions.card7.title': 'SBA Programs',
    'solutions.card7.desc': 'Government-backed loans with terms up to 25 years.',
    'solutions.card8.title': 'Startup Capital',
    'solutions.card8.desc': 'For new businesses with solid credit (750+ FICO required).',
    'solutions.cta': 'SCHEDULE CONSULTATION',
    // How It Works
    'howItWorks.title': 'Get Funding in',
    'howItWorks.titleHighlight': '3 Simple Steps',
    'howItWorks.subtitle': 'Fast, transparent, and hassle-free',
    'howItWorks.step1.title': 'Apply Online',
    'howItWorks.step1.desc': 'Complete a quick 2-minute application.',
    'howItWorks.step2.title': 'We Compare Offers',
    'howItWorks.step2.desc': 'We present your profile to 100+ lenders to negotiate the best terms.',
    'howItWorks.step3.title': 'Receive Your Funds',
    'howItWorks.step3.desc': 'Choose the ideal offer and receive capital in as little as 24 hours.',
    'howItWorks.cta': 'SCHEDULE CONSULTATION',
    // Requirements
    'requirements.title': 'Who',
    'requirements.titleHighlight': 'Qualifies?',
    'requirements.subtitle': 'Most businesses qualify with these basic requirements:',
    'requirements.req1.title': '12+ Months in Operation',
    'requirements.req2.title': '$10,000+ Monthly Revenue',
    'requirements.req3.title': 'FICO Score 500+',
    'requirements.req4.title': 'Legal Entity (LLC or Corp)',
    'requirements.req5.title': '3 Months Bank Statements',
    'requirements.cta': 'SCHEDULE CONSULTATION',
    // Testimonials
    'testimonials.title': 'What Our Clients',
    'testimonials.titleHighlight': 'Say About Us',
    'testimonials.subtitle': 'Real experiences from real business owners.',
    // Trust & Security
    'trust.title': 'Your Trusted Partner in',
    'trust.titleHighlight': 'Business Funding',
    'trust.subtitle': 'We work for you, not the banks.',
    'trust.item1.title': 'Brokers, Not Lenders',
    'trust.item1.desc': 'We work for you, not the banks.',
    'trust.item2.title': 'No Upfront Fees',
    'trust.item2.desc': 'We only charge if you get funded.',
    'trust.item3.title': 'Verified Lenders',
    'trust.item3.desc': 'Every partner is thoroughly evaluated.',
    'trust.item4.title': 'Secure Application',
    'trust.item4.desc': '256-bit bank-level encryption.',
    'trust.item5.title': 'Transparent Process',
    'trust.item5.desc': 'No fine print or surprises.',
    'trust.cta': 'SCHEDULE CONSULTATION',
    // FAQ
    'faq.title': 'Frequently Asked',
    'faq.titleHighlight': 'Questions',
    // Contact
    'contact.title': 'Speak With a',
    'contact.titleHighlight': 'Funding Specialist',
    'contact.subtitle': 'Ready to grow your business? Fill out the form or contact us directly. Our team is standing by to answer your questions.',
    'contact.address': 'Company Address',
    'contact.phone': 'Talk To Us',
    'contact.hours': 'Office Hours',
    'contact.hoursValue': 'Mon-Fri 9am-5pm EST',
    'contact.email': 'Email Us',
    'contact.formTitle': 'Get a Free Consultation',
    'contact.firstName': 'First Name',
    'contact.lastName': 'Last Name',
    'contact.emailLabel': 'Email',
    'contact.phoneLabel': 'Phone',
    'contact.company': 'Company Name',
    'contact.interested': 'Interested In',
    'contact.selectOption': 'Select a funding option...',
    'contact.message': 'Additional Message',
    'contact.messagePlaceholder': 'Tell us about your funding needs...',
    'contact.submit': 'SCHEDULE CONSULTATION',
    'contact.successTitle': 'Request Received!',
    'contact.successMessage': 'Redirecting you to WhatsApp to complete your request...',
    // Footer
    'footer.description': 'Empowering businesses with fast, transparent, and flexible funding solutions.',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact',
    'footer.hoursTitle': 'Hours of Operation',
    'footer.hoursDay': 'Monday - Friday',
    'footer.hoursTime': '9:00 AM - 5:00 PM EST',
    'footer.hoursClosed': 'Closed on Weekends',
    'footer.disclaimer1': 'Brixen Funding LLC is a financial brokerage service, not a direct lender. All financing offers, approvals, and funding amounts are issued by third-party lenders in our network and are subject to lender requirements.',
    'footer.disclaimer2': 'This site is not affiliated with or endorsed by Facebook or Meta Platforms, Inc. "Facebook" and "Meta" are registered trademarks of Meta Platforms, Inc.',
    'footer.disclaimer3': 'Information shared on this website is for informational purposes only and does not constitute legal or financial advice. Results vary by individual business profile. By using this site or our services, you agree to our Terms & Conditions and Privacy Policy, and consent to receive communications (including SMS and calls) from Brixen Funding LLC under applicable laws.',
    'footer.copyright': '© 2025 Brixen Funding. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions'
  },
  es: {
    // Navbar
    'nav.aboutUs': 'Nosotros',
    'nav.funding': 'Financiamiento',
    'nav.howItWorks': 'Cómo Funciona',
    'nav.requirements': 'Requisitos',
    'nav.testimonials': 'Testimonios',
    'nav.faq': 'FAQ',
    'nav.applyNow': 'AGENDAR CONSULTA',
    // Hero
    'hero.badge': 'Financiamiento Comercial',
    'hero.title': 'Financiamiento Comercial para Dueños de Negocio en Estados Unidos',
    'hero.titleHighlight': '',
    'hero.titleSuffix': '',
    'hero.subtitle': 'Ofrecemos opciones de financiamiento para todo tipo de negocio. Evaluamos las mejores alternativas disponibles para ti con un proceso simple y personalizado.',
    'hero.cta': 'APLICAR AHORA',
    'hero.ctaSecondary': 'AGENDAR CONSULTA',
    'hero.microcopy1': 'Sin afectar tu crédito personal',
    'hero.microcopy2': 'Sin fee de iniciación',
    'hero.microcopy3': 'Aprobaciones en 48 hrs',
    'hero.trustedBy': 'Confiado por 5,000+ Dueños',
    'hero.trustpilot': 'Excelente en Trustpilot',
    'hero.approvalBadge1': 'Financiamiento Aprobado',
    'hero.approvalBadge2': 'Aprobado',
    'hero.approvalBadge3': 'Aprobado',
    // Benefits Ticker
    'ticker.fastApprovals': 'Respuestas Rápidas',
    'ticker.noUpfront': 'Sin Cargos Iniciales',
    'ticker.softCredit': 'Consulta de Crédito Suave',
    'ticker.flexible': 'Términos Flexibles',
    'ticker.sameDay': 'Fondos el Mismo Día',
    'ticker.approval': 'Múltiples Opciones',
    // Value Proposition
    'value.title': 'La Forma Más Simple de Obtener Capital',
    'value.titleHighlight': 'para tu Negocio',
    'value.subtitle': 'En Brixen Funding simplificamos el financiamiento comercial conectándote con prestamistas según tu perfil.',
    'value.feature1.title': 'Prestamistas Verificados',
    'value.feature1.desc': 'Acceso a más de 100 socios de financiamiento verificados.',
    'value.feature2.title': 'Financiamiento a Medida',
    'value.feature2.desc': 'Soluciones adaptadas a las necesidades reales de tu negocio.',
    'value.feature3.title': 'Respuestas Rápidas',
    'value.feature3.desc': 'Opciones disponibles en tan solo 24 horas.',
    'value.feature4.title': 'Sin Garantía Personal',
    'value.feature4.desc': '(en la mayoría de los casos)',
    // Funding Solutions
    'solutions.title': 'Soluciones de Financiamiento',
    'solutions.titleHighlight': 'para Cada Negocio',
    'solutions.subtitle': 'Te conectamos con la mejor opción según tu industria, ingresos y objetivos.',
    'solutions.card1.title': 'Líneas de Crédito Comercial',
    'solutions.card1.desc': 'Capital rotativo. Paga solo lo que utilizas.',
    'solutions.card2.title': 'Crédito Comercial al 0% de Interés',
    'solutions.card2.desc': 'Hasta $150,000 con 0% APR por 12 meses (sujeto a calificación).',
    'solutions.card3.title': 'Capital de Trabajo',
    'solutions.card3.desc': 'Liquidez rápida para nómina, inventario o expansión.',
    'solutions.card4.title': 'Financiamiento Basado en Ingresos',
    'solutions.card4.desc': 'Pagos flexibles basados en ventas. Se acepta crédito bajo.',
    'solutions.card5.title': 'Financiamiento de Equipos',
    'solutions.card5.desc': 'Adquiere maquinaria, vehículos o tecnología sin descapitalizarte.',
    'solutions.card6.title': 'Préstamos a Plazo',
    'solutions.card6.desc': 'Opciones tradicionales con pagos fijos y plazos extendidos.',
    'solutions.card7.title': 'Programas SBA',
    'solutions.card7.desc': 'Préstamos respaldados por el gobierno con plazos de hasta 25 años.',
    'solutions.card8.title': 'Capital Inicial (Startups)',
    'solutions.card8.desc': 'Para nuevos negocios con crédito sólido (750+ FICO requerido).',
    'solutions.cta': 'AGENDAR CONSULTA',
    // How It Works
    'howItWorks.title': 'Obtén Financiamiento en',
    'howItWorks.titleHighlight': '3 Simples Pasos',
    'howItWorks.subtitle': 'Rápido, transparente y sin complicaciones',
    'howItWorks.step1.title': 'Aplica en Línea',
    'howItWorks.step1.desc': 'Completa una aplicación rápida de 2 minutos.',
    'howItWorks.step2.title': 'Comparamos Ofertas',
    'howItWorks.step2.desc': 'Presentamos tu perfil a más de 100 prestamistas para encontrar las mejores opciones.',
    'howItWorks.step3.title': 'Recibe tus Fondos',
    'howItWorks.step3.desc': 'Elige la oferta que mejor se adapte a tu negocio.',
    'howItWorks.cta': 'AGENDAR CONSULTA',
    // Requirements
    'requirements.title': '¿Quién',
    'requirements.titleHighlight': 'Califica?',
    'requirements.subtitle': 'La mayoría de los negocios califican con estos requisitos básicos:',
    'requirements.req1.title': '12+ meses en operación',
    'requirements.req2.title': '$10,000+ en ingresos mensuales',
    'requirements.req3.title': 'Puntaje FICO 500+',
    'requirements.req4.title': 'Entidad legal (LLC o Corp)',
    'requirements.req5.title': '3 meses de estados de cuenta',
    'requirements.cta': 'AGENDAR CONSULTA',
    // Testimonials
    'testimonials.title': 'Lo Que Dicen',
    'testimonials.titleHighlight': 'Nuestros Clientes',
    'testimonials.subtitle': 'Experiencias reales de dueños de negocio como tú.',
    // Trust & Security
    'trust.title': 'Tu Socio de Confianza en',
    'trust.titleHighlight': 'Financiamiento Comercial',
    'trust.subtitle': 'Trabajamos para ti, no para los bancos.',
    'trust.item1.title': 'Brokers, No Prestamistas',
    'trust.item1.desc': 'Trabajamos para ti, no para los bancos.',
    'trust.item2.title': 'Sin Cargos Iniciales',
    'trust.item2.desc': 'Solo cobramos si obtienes financiamiento.',
    'trust.item3.title': 'Prestamistas Verificados',
    'trust.item3.desc': 'Cada socio es evaluado minuciosamente.',
    'trust.item4.title': 'Aplicación Segura',
    'trust.item4.desc': 'Encriptación bancaria 256-bit.',
    'trust.item5.title': 'Proceso Transparente',
    'trust.item5.desc': 'Sin letras pequeñas ni sorpresas.',
    'trust.cta': 'AGENDAR CONSULTA',
    // FAQ
    'faq.title': 'Preguntas',
    'faq.titleHighlight': 'Frecuentes',
    // Contact
    'contact.title': 'Habla con un',
    'contact.titleHighlight': 'Especialista en Financiamiento',
    'contact.subtitle': '¿Listo para hacer crecer tu negocio? Completa el formulario o contáctanos directamente. Nuestro equipo está listo para ayudarte.',
    'contact.address': 'Dirección',
    'contact.phone': 'Llámanos',
    'contact.hours': 'Horario',
    'contact.hoursValue': 'Lun-Vie 9am-5pm EST',
    'contact.email': 'Escríbenos',
    'contact.formTitle': 'Obtén una Consulta Gratuita',
    'contact.firstName': 'Nombre',
    'contact.lastName': 'Apellido',
    'contact.emailLabel': 'Correo Electrónico',
    'contact.phoneLabel': 'Teléfono',
    'contact.company': 'Nombre de la Empresa',
    'contact.interested': 'Interesado En',
    'contact.selectOption': 'Selecciona una opción...',
    'contact.message': 'Mensaje Adicional',
    'contact.messagePlaceholder': 'Cuéntanos sobre tus necesidades de financiamiento...',
    'contact.submit': 'AGENDAR CONSULTA',
    'contact.successTitle': '¡Solicitud Recibida!',
    'contact.successMessage': 'Redirigiendo a WhatsApp para completar tu solicitud...',
    // Footer
    'footer.description': 'Conectando negocios con soluciones de financiamiento rápidas, transparentes y flexibles.',
    'footer.quickLinks': 'Enlaces Rápidos',
    'footer.contact': 'Contacto',
    'footer.hoursTitle': 'Horario de Atención',
    'footer.hoursDay': 'Lunes - Viernes',
    'footer.hoursTime': '9:00 AM - 5:00 PM EST',
    'footer.hoursClosed': 'Cerrado Fines de Semana',
    'footer.disclaimer1': 'Brixen Funding LLC es un servicio de corretaje financiero, no un prestamista directo. Todas las ofertas de financiamiento, aprobaciones y montos son emitidos por prestamistas de terceros en nuestra red y están sujetos a sus requisitos.',
    'footer.disclaimer2': 'Este sitio no está afiliado ni respaldado por Facebook o Meta Platforms, Inc. "Facebook" y "Meta" son marcas registradas de Meta Platforms, Inc.',
    'footer.disclaimer3': 'La información compartida en este sitio web es solo para fines informativos y no constituye asesoramiento legal o financiero. Los resultados varían según el perfil de cada negocio. Al usar este sitio o nuestros servicios, aceptas nuestros Términos y Condiciones y Política de Privacidad.',
    'footer.copyright': '© 2025 Brixen Funding. Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos y Condiciones'
  }
};
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);
export function TranslationProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lang, setLang] = useState<Language>('es');
  const t = (key: string): string => {
    return translations[lang][key] || key;
  };
  return <TranslationContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-539">
      {children}
    </TranslationContext.Provider>;
}
export function useTranslations() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
}